import { groupBy } from 'lodash';
import { ApSimpleDropdown } from '@alixpartners/ui-components';
import React from 'react';
import { DEFAULT_CURRENCY_FORMAT, PERMISSIONS } from 'utils/constants';
import {
  getColorIndex,
  getFormattedCurrency,
  getFormattedPercentage,
} from 'utils/format';
import {
  IApplicationSpendItem,
  ICurrencyFormat,
  IDisplayByType,
} from 'utils/types';
import * as htmlToImage from 'html-to-image';
import { downloadAsFile } from 'utils/download';

import { PieChart } from './PieChart';
import {
  ChartDescription,
  ChartDescriptionMain,
  ChartDescriptionSecondary,
  ChartContainer,
  ChartWrapper,
  Container,
  Header,
  HeaderDropdownContainer,
  HeaderLabel,
  NoPermissions,
} from './PieChart.styles';
import { EXPORT_OPTIONS } from 'utils/constants';
import moment from 'moment';

const COLORS = ['#032E45', '#0696A6'];

export const SplitApplicationsContainer = ({
  applications,
  displayBy,
  downloadApplicationContributionData,
  format,
  permissions,
  selectedGroup,
  selectedApplication,
  showSuccessNotification,
}: {
  applications: IApplicationSpendItem[];
  displayBy: IDisplayByType;
  downloadApplicationContributionData: () => Promise<any>;
  format: ICurrencyFormat;
  permissions: string[];
  selectedGroup?: string;
  selectedApplication?: string;
  showSuccessNotification: () => void;
}) => {
  const chartContainerId = `TXR_spend_split-${moment().format()}`;
  const onExportHtmlChartAsPngClick = () => {
    const chart = document.getElementById(chartContainerId);
    if (chart) {
      htmlToImage.toPng(chart).then((dataUrl) => {
        downloadAsFile(dataUrl, chartContainerId);
      });
    }
  };

  let filteredApplications = applications;
  if (selectedApplication) {
    filteredApplications = applications.filter(
      (app) => app.applicationName === selectedApplication
    );
  } else if (selectedGroup) {
    filteredApplications = applications.filter(
      (app) => app.groupName === selectedGroup
    );
  }
  const groupedApps = groupBy(filteredApplications, 'contribution');
  const total = filteredApplications.reduce((acc, app) => acc + app.spend, 0);
  const getYValue = (value: number, total: number) => (value / total) * 100;
  const pieData = Object.keys(groupedApps).map((contribution) => {
    const contributionApps = groupedApps[contribution];
    const value = contributionApps.reduce((acc, app) => acc + app.spend, 0);
    const yValue = getYValue(value, total);
    return {
      name: contribution,
      value: getFormattedPercentage(yValue, format),
      rawValue: value,
      y: yValue,
      spend: getFormattedCurrency({
        currencyFormat: format,
        value,
        isCurrencySign: true,
        isIdealDenomination: true,
      }),
      color: COLORS[getColorIndex(contribution)],
    };
  });

  let descriptionMain = '';
  let descriptionSecondary = 'of total spend';
  if (pieData) {
    const thirdPartyData = pieData.find((data) => data.name === 'ThirdParty');
    const personnelData = pieData.find((data) => data.name === 'Personnel');
    const contribution1Value = thirdPartyData
      ? parseInt(
          getFormattedPercentage(thirdPartyData.y, DEFAULT_CURRENCY_FORMAT)
        )
      : '0';
    const contribution2Value = personnelData
      ? parseInt(
          getFormattedPercentage(personnelData.y, DEFAULT_CURRENCY_FORMAT)
        )
      : '0';
    descriptionMain = `${contribution1Value}${
      contribution2Value ? `/${contribution2Value}` : ''
    }%`;
  }

  let headerLabel: React.ReactNode = 'Applications total spend split';
  if (selectedGroup) {
    headerLabel = <span>{selectedGroup} applications total spend split</span>;
    descriptionSecondary = 'of group spend';
  }
  if (selectedApplication) {
    headerLabel = <span>{selectedApplication} application spend split</span>;
    descriptionSecondary = 'of application spend';
  }

  return (
    <Container>
      {permissions.includes(PERMISSIONS.APPLICATIONS_PIE_VIEW) ? (
        <React.Fragment>
          <Header>
            <HeaderLabel>{headerLabel}</HeaderLabel>
            <HeaderDropdownContainer>
              <ApSimpleDropdown
                iconName="outline_save_alt"
                onChange={(opt: any) => {
                  if (opt.value === 'png') {
                    onExportHtmlChartAsPngClick();
                  } else if (opt.value === 'xlsx') {
                    downloadApplicationContributionData().then(
                      showSuccessNotification
                    );
                  }
                }}
                options={EXPORT_OPTIONS}
                label="Export"
              />
            </HeaderDropdownContainer>
          </Header>
          <ChartContainer>
            <ChartWrapper id={chartContainerId}>
              <ChartDescription>
                <ChartDescriptionMain>{descriptionMain}</ChartDescriptionMain>
                <ChartDescriptionSecondary>
                  {descriptionSecondary}
                </ChartDescriptionSecondary>
              </ChartDescription>
              <PieChart data={pieData} />
            </ChartWrapper>
          </ChartContainer>
        </React.Fragment>
      ) : (
        <NoPermissions>No permission to view chart</NoPermissions>
      )}
    </Container>
  );
};
